import './LegalPage.css';

function TermsOfService() {
  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <div className="legal-nav-container">
          <a href="/" className="legal-nav-brand">
            <img src={process.env.PUBLIC_URL + '/favicon.png'} alt="Advanced Bowling Tracker" className="legal-nav-icon" />
            <span>Advanced Bowling Tracker</span>
          </a>
        </div>
      </nav>

      <div className="legal-content">
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last updated: August 2, 2026</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing or using Advanced Bowling Tracker ("the Service"), including the mobile application and website, you agree to be bound by these Terms of Service ("Terms"). If you do not agree to these Terms, please do not use the Service.
          </p>
        </section>

        <section>
          <h2>2. Description of Service</h2>
          <p>
            Advanced Bowling Tracker is a bowling score tracking application that provides frame-by-frame scoring, statistics, league management, social features, and related tools for bowlers. The Service is available on iOS (iPhone and Apple Watch) and Android.
          </p>
        </section>

        <section>
          <h2>3. Accounts</h2>
          <p>
            Some features of the Service require you to create an account. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
          </p>
          <p>
            You may sign in using third-party authentication providers such as Apple or Google. By doing so, you authorize us to access certain account information from those providers as described in our <a href="/privacy">Privacy Policy</a>.
          </p>
          <p>
            You agree to provide accurate and complete information when creating your account and to update your information as needed.
          </p>
          <p>
            <strong>Age requirement for social features.</strong> The Service's social features &mdash; the activity feed, public profiles and user search, following other bowlers, comments and reactions, live score sharing and spectating, and publishing your scores &mdash; are available only to users aged 13 or older, and we ask you to confirm your age before enabling them. Bowlers of any age may use the Service to track their own scores, statistics, leagues, equipment, and practice. See <a href="/privacy">Age Confirmation and Children's Privacy</a> in our Privacy Policy for what we store and how to change it.
          </p>
        </section>

        <section>
          <h2>4. Free and Pro Tiers</h2>

          <h3>Free Tier</h3>
          <p>The free tier of the Service includes:</p>
          <ul>
            <li>Unlimited game score logging</li>
            <li>Up to 15 shot-by-shot games per month</li>
            <li>League management and team features</li>
            <li>Social features (following friends, reactions, comments)</li>
            <li>Statistics and trend tracking</li>
            <li>Bowling ball catalog and arsenal management</li>
            <li>Oil pattern library</li>
            <li>Apple Watch support</li>
            <li>Live Activities</li>
          </ul>

          <h3>Pro Subscription</h3>
          <p>The Pro subscription unlocks additional features, including:</p>
          <ul>
            <li>Unlimited shot-by-shot game recording</li>
            <li>Access to the full library of practice drills</li>
            <li>Additional features as they become available</li>
          </ul>
          <p>
            Pro subscriptions are billed through the Apple App Store (iOS) or Google Play (Android) and are subject to that store's subscription terms. You can manage or cancel your subscription at any time through your Apple ID or Google Play account settings.
          </p>
        </section>

        <section>
          <h2>5. User Content</h2>
          <p>
            You retain ownership of all content you submit through the Service, including game scores, notes, comments, and reactions ("User Content").
          </p>
          <p>
            By posting User Content through social features, you grant us a non-exclusive, worldwide, royalty-free license to display that content to other users as part of the Service's social functionality (e.g., showing your scores to your followers).
          </p>
          <p>You agree not to post content that:</p>
          <ul>
            <li>Is unlawful, harmful, threatening, abusive, or harassing</li>
            <li>Infringes on the intellectual property rights of others</li>
            <li>Contains spam, malware, or deceptive content</li>
            <li>Impersonates another person or entity</li>
          </ul>
          <p>
            We reserve the right to remove any User Content that violates these Terms.
          </p>
          <h3>Community Guidelines</h3>
          <p>
            We have <strong>no tolerance</strong> for objectionable content or abusive users. You can <strong>report</strong> content or another user, and <strong>block</strong> any user, from within the app. We review reports and may remove content, or suspend or terminate accounts, that violate these Terms or Community Guidelines.
          </p>
        </section>

        <section>
          <h2>6. Acceptable Use</h2>
          <p>You agree not to:</p>
          <ul>
            <li>Use the Service for any unlawful purpose</li>
            <li>Attempt to gain unauthorized access to the Service or its systems</li>
            <li>Interfere with or disrupt the Service or its infrastructure</li>
            <li>Reverse engineer, decompile, or disassemble any part of the Service</li>
            <li>Use automated tools to scrape or collect data from the Service</li>
            <li>Create multiple accounts for deceptive purposes</li>
          </ul>
        </section>

        <section>
          <h2>7. Intellectual Property</h2>
          <p>
            The Service, including its design, features, code, and content (excluding User Content), is owned by Joseph Miller and is protected by copyright and other intellectual property laws. You may not copy, modify, distribute, or create derivative works from the Service without our written permission.
          </p>
        </section>

        <section>
          <h2>8. Disclaimers</h2>
          <p>
            The Service is provided "as is" and "as available" without warranties of any kind, either express or implied, including but not limited to warranties of merchantability, fitness for a particular purpose, or non-infringement.
          </p>
          <p>
            We do not guarantee that the Service will be uninterrupted, error-free, or that any data will be preserved indefinitely. While we take reasonable steps to protect your data, you are encouraged to maintain your own records.
          </p>
        </section>

        <section>
          <h2>9. Limitation of Liability</h2>
          <p>
            To the maximum extent permitted by law, Joseph Miller and Advanced Bowling Tracker shall not be liable for any indirect, incidental, special, consequential, or punitive damages arising out of or related to your use of the Service, regardless of the theory of liability.
          </p>
        </section>

        <section>
          <h2>10. Account Termination</h2>
          <p>
            You may delete your account at any time from within the app or by contacting us at <a href="mailto:support@advancedbowlingtracker.com">support@advancedbowlingtracker.com</a>. Upon deletion, your personal data will be removed in accordance with our <a href="/privacy">Privacy Policy</a>.
          </p>
          <p>
            We reserve the right to suspend or terminate accounts that violate these Terms, with or without notice.
          </p>
        </section>

        <section>
          <h2>11. Changes to These Terms</h2>
          <p>
            We may update these Terms from time to time. We will notify you of significant changes by posting the updated Terms on this page and updating the "Last updated" date. Your continued use of the Service after changes are posted constitutes acceptance of the revised Terms.
          </p>
        </section>

        <section>
          <h2>12. Governing Law</h2>
          <p>
            These Terms shall be governed by and construed in accordance with the laws of the United States, without regard to conflict of law principles.
          </p>
        </section>

        <section>
          <h2>13. Contact Us</h2>
          <p>
            If you have questions about these Terms, please contact us at:
          </p>
          <p>
            <a href="mailto:support@advancedbowlingtracker.com">support@advancedbowlingtracker.com</a>
          </p>
        </section>
      </div>

      <footer className="legal-footer">
        <p>&copy; {new Date().getFullYear()} Joseph Miller. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default TermsOfService;
